import React, { useState, useEffect } from 'react';
import { FiTrendingUp, FiAlertCircle, FiCheckCircle, FiUsers, FiPlus } from 'react-icons/fi';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './NutritionOverview.css';

const NutritionOverview = ({ coachId, onCreatePlan, onViewReports }) => {
  const [stats, setStats] = useState({
    activePlans: 0,
    avgCompliance: 0,
    lowComplianceClients: [],
    totalClients: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNutritionStats();
  }, [coachId]);

  const fetchNutritionStats = async () => {
    if (!coachId) return;

    try {
      setLoading(true);

      // Fetch active nutrition plans
      const plansRef = collection(db, 'nutritionPlans');
      const plansQuery = query(
        plansRef,
        where('coachId', '==', coachId),
        where('status', '==', 'active')
      );
      const plansSnapshot = await getDocs(plansQuery);
      const activePlans = plansSnapshot.size;

      // Fetch meal logs for the past week to calculate compliance
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

      const logsRef = collection(db, 'mealLogs');
      const logsQuery = query(
        logsRef,
        where('trainerId', '==', coachId),
        where('timestamp', '>=', oneWeekAgo)
      );
      const logsSnapshot = await getDocs(logsQuery);

      // Calculate compliance by client
      const clientCompliance = {};
      logsSnapshot.forEach(doc => {
        const data = doc.data();
        if (!clientCompliance[data.userId]) {
          clientCompliance[data.userId] = {
            clientId: data.userId,
            clientName: data.userName || 'Unknown',
            logged: 0,
            expected: 7 * 3 // 3 meals per day for 7 days
          };
        }
        clientCompliance[data.userId].logged += 1;
      });

      // Calculate average compliance and find low compliance clients
      const clientsWithData = Object.values(clientCompliance);
      const avgCompliance = clientsWithData.length > 0
        ? clientsWithData.reduce((sum, client) => {
            const compliance = (client.logged / client.expected) * 100;
            return sum + compliance;
          }, 0) / clientsWithData.length
        : 0;

      const lowComplianceClients = clientsWithData
        .filter(client => {
          const compliance = (client.logged / client.expected) * 100;
          return compliance < 70;
        })
        .map(client => ({
          ...client,
          compliance: Math.round((client.logged / client.expected) * 100)
        }));

      setStats({
        activePlans,
        avgCompliance: Math.round(avgCompliance),
        lowComplianceClients,
        totalClients: clientsWithData.length
      });
    } catch (error) {
      console.error('Error fetching nutrition stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="nutrition-overview-widget loading">
        <div className="widget-header">
          <h3>Nutrition Overview</h3>
        </div>
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="nutrition-overview-widget">
      <div className="widget-header">
        <h3>Nutrition Overview</h3>
        <span className="week-indicator">This Week</span>
      </div>

      <div className="overview-stats">
        <div className="stat-card primary">
          <div className="stat-icon">
            <FiUsers />
          </div>
          <div className="stat-content">
            <h4>{stats.activePlans}</h4>
            <p>Active Plans</p>
          </div>
        </div>

        <div className="stat-card compliance">
          <div className="stat-icon">
            <FiTrendingUp />
          </div>
          <div className="stat-content">
            <h4>{stats.avgCompliance}%</h4>
            <p>Avg Compliance</p>
          </div>
          <div className={`compliance-indicator ${stats.avgCompliance >= 80 ? 'good' : stats.avgCompliance >= 60 ? 'medium' : 'low'}`}>
            {stats.avgCompliance >= 80 ? <FiCheckCircle /> : <FiAlertCircle />}
          </div>
        </div>
      </div>

      {stats.lowComplianceClients.length > 0 && (
        <div className="alert-section">
          <div className="alert-header">
            <FiAlertCircle />
            <span>Low Compliance Alerts</span>
          </div>
          <div className="alert-list">
            {stats.lowComplianceClients.slice(0, 3).map((client, index) => (
              <div key={index} className="alert-item">
                <span className="client-name">{client.clientName}</span>
                <span className="compliance-badge low">{client.compliance}%</span>
              </div>
            ))}
            {stats.lowComplianceClients.length > 3 && (
              <div className="alert-item more">
                +{stats.lowComplianceClients.length - 3} more clients need attention
              </div>
            )}
          </div>
        </div>
      )}

      <div className="widget-actions">
        <button className="btn-primary" onClick={onCreatePlan}>
          <FiPlus />
          Create Plan
        </button>
        <button className="btn-secondary" onClick={onViewReports}>
          View Reports
        </button>
      </div>
    </div>
  );
};

export default NutritionOverview;
